import React from 'react';
import Autocomplete, {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import {
    Box,
    InputAdornment,
    Chip,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface SearchBarProps {
    placeholder?: string;
    onChange?:
        | ((value: React.SetStateAction<string[]>) => void)
        | ((
              value: React.SetStateAction<string[]>,
              reason: AutocompleteChangeReason,
              details: string,
          ) => void);
    onInputChange?: (value: React.SetStateAction<string>) => void;
    options?: string[];
    values?: string[];
    inputValue?: string;
    label?: string | React.ReactNode;
    tooltip?: string | React.ReactNode;
    helperText?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
    placeholder = '',
    values = [],
    options = [],
    onChange = (): void => null,
    onInputChange = (): void => null,
    inputValue = '',
    label = undefined,
    tooltip = undefined,
    helperText = '',
}) => (
    <Box flexGrow={1}>
        {label || tooltip ? (
            <Box display="flex" gap={1} alignItems="center" marginBottom="12px">
                {label ? (
                    <Typography variant="subtitle">{label}</Typography>
                ) : null}
                {tooltip ? (
                    <Tooltip placement="top" title={tooltip} arrow>
                        <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                ) : null}
            </Box>
        ) : null}
        <Autocomplete
            multiple
            options={options}
            freeSolo
            value={values}
            inputValue={inputValue}
            onChange={(
                _e,
                value: string[],
                reason: AutocompleteChangeReason,
                details: AutocompleteChangeDetails<string>,
            ) => onChange(value, reason, details?.option)}
            onInputChange={(_e, value) => onInputChange(value)}
            renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                    <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <Box
                                        sx={(theme) => ({
                                            padding: theme.spacing(1),
                                            display: 'flex',
                                            alignItems: 'center',
                                        })}
                                    >
                                        <SearchIcon color="primary" />
                                    </Box>
                                </InputAdornment>
                                <>{params.InputProps.startAdornment}</>
                            </>
                        ),
                        endAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <Box
                                        sx={(theme) => ({
                                            padding: theme.spacing(1),
                                            display: 'flex',
                                            alignItems: 'center',
                                        })}
                                    >
                                        {helperText?.length > 0 ? (
                                            <Tooltip title={helperText}>
                                                <InfoIcon color="secondary" />
                                            </Tooltip>
                                        ) : null}
                                    </Box>
                                </InputAdornment>
                                <>{params.InputProps.endAdornment}</>
                            </>
                        ),
                    }}
                />
            )}
        />
    </Box>
);

export default SearchBar;
